import { openDB } from 'idb'

// On-device store for uploaded book files (PDF/EPUB). The blob never
// leaves the device — only lightweight metadata (file_format/name/size)
// is synced on the reading_books row. Keyed by the book's id so the
// reader can pull the bytes back by book.
//
// Mirrors the localPhotos.js pattern (idb, single object store).

const DB_NAME = 'phoenix-reading-files'
const DB_VERSION = 1
const STORE = 'book-files'

let dbPromise = null
function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          // keyPath = bookId. Record: { bookId, userId, blob, fileName,
          // mimeType, format, size, createdAt }.
          db.createObjectStore(STORE, { keyPath: 'bookId' })
        }
      },
    })
  }
  return dbPromise
}

// Store (or replace) the file for a book.
export async function putBookFile(bookId, { blob, userId, fileName, mimeType, format, size }) {
  if (!bookId || !blob) return false
  const db = await getDB()
  await db.put(STORE, {
    bookId,
    userId: userId || null,
    blob,
    fileName: fileName || null,
    mimeType: mimeType || blob.type || null,
    format: format || null,
    size: size ?? blob.size ?? null,
    createdAt: Date.now(),
  })
  return true
}

// Full record (incl. blob) or null if this device doesn't have it.
export async function getBookFile(bookId) {
  if (!bookId) return null
  const db = await getDB()
  return (await db.get(STORE, bookId)) || null
}

// Cheap presence check without pulling the blob into memory.
export async function hasBookFile(bookId) {
  if (!bookId) return false
  const db = await getDB()
  const key = await db.getKey(STORE, bookId)
  return key != null
}

export async function deleteBookFile(bookId) {
  if (!bookId) return
  const db = await getDB()
  await db.delete(STORE, bookId)
}

// Rough on-device usage for these book files (sum of stored sizes).
export async function estimateUsage() {
  const db = await getDB()
  const all = await db.getAll(STORE)
  return all.reduce((sum, r) => sum + (r.size || 0), 0)
}

// Detect 'pdf' | 'epub' from a File, or null if unsupported.
export function detectBookFormat(file) {
  if (!file) return null
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  if (type === 'application/epub+zip' || name.endsWith('.epub')) return 'epub'
  return null
}
